'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import {
  PREFERENCE_KEY,
  isLocale,
  localeForLanguages,
  preferenceCookie,
  readPreference,
} from './locale';
import { translate } from './translate';

const LocaleContext = createContext({
  locale: 'es',
  ready: false,
  setLocale: () => {},
});

export default function LocaleProvider({ children }) {
  const [locale, updateLocale] = useState('es');
  const [ready, setReady] = useState(false);
  const setLocale = (next) => {
    if (!isLocale(next)) return;
    try {
      window.localStorage.setItem(PREFERENCE_KEY, next);
    } catch {
      /* Storage may be blocked. */
    }
    try {
      document.cookie = preferenceCookie(
        next,
        window.location.hostname,
        window.location.protocol === 'https:'
      );
    } catch {
      /* The selection still works with cookies disabled. */
    }
    updateLocale(next);
  };

  useEffect(() => {
    setReady(true);
    let storage;
    try {
      storage = window.localStorage;
    } catch {
      /* Cookies still work. */
    }
    const onStorage = (event) => {
      if (event.key === PREFERENCE_KEY && isLocale(event.newValue)) {
        updateLocale(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    const saved = readPreference(storage, document.cookie);
    if (saved) {
      updateLocale(saved);
      return () => window.removeEventListener('storage', onStorage);
    }
    updateLocale(
      localeForLanguages(
        navigator.languages?.length ? navigator.languages : [navigator.language]
      )
    );
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    window.dispatchEvent(
      new CustomEvent('bison:locale-change', { detail: locale })
    );
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, ready }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
export function useTranslation() {
  const { locale } = useLocale();
  return (text, values) => translate(locale, text, values);
}

// A text-only React leaf keeps the existing server/client component boundaries intact.
export function T({ children, values }) {
  const t = useTranslation();
  return typeof children === 'string' ? t(children, values) : children;
}
